<template lang="pug">
div
  el-row
    el-col
      h3.title Mercado Pago
  el-row
    el-col
      el-form(:inline="true" label-position="top" size="mini")
        el-form-item(label="Desde:")
          date-input(:inputModel="search" modelKey="date" type="date")
        el-form-item(label="País:")
          select-input(:options="countries" :attribute="search" modelKey="country")
        el-form-item.filters__buttons-wrapper
          el-button(
            type="primary"
            icon="el-icon-search"
            @click.prevent="fetchMercadoPago()"
          ) FILTRAR
  el-row(v-if="hasData")
    el-col(v-if="paymentMethodsItems && paymentMethodsItems.length > 0")
      h4.title-section ANÁLISIS DE MÉTODOS DE PAGO - {{ countryName }}
      data-table(
        :headers="paymentMethodsHeaders"
        :items="paymentMethodsItems"
        :loading="loading"
        :shownActions="false"
        :height="300"
      )
    el-col(v-if="operatorsItems && operatorsItems.length > 0")
      h4.title-section ANÁLISIS DE OPERADORES - MERCADO PAGO
      p.subtitle Datos desde el lanzamiento de Mercado Pago (24 de diciembre de 2025)
      data-table(
        :headers="operatorsHeaders"
        :items="operatorsItems"
        :loading="loading"
        :shownActions="false"
        :height="400"
      )
    el-col(v-if="dailyAnalysisItems && dailyAnalysisItems.length > 0")
      h4.title-section ANÁLISIS POR DÍA - OPERADOR
      div(v-for="(dayData, index) in dailyAnalysisItems" :key="index")
        div.day-section
          p.day-title Fecha: {{ formatDate(dayData.date) }}
          p.growth-text(v-if="dayData.growth") Crecimiento MP: {{ formatGrowth(dayData.growth) }}
        data-table(
          :headers="operatorsHeaders"
          :items="dayData.operators"
          :loading="loading"
          :shownActions="false"
          :height="300"
        )
  el-row(v-else-if="!loading")
    el-col
      el-alert(
        title="No hay datos disponibles"
        type="info"
        :closable="false"
      )
</template>

<script>
import DateInput from '@/components/forms/inputs/DateInput.vue';
import SelectInput from '@/components/forms/inputs/SelectInput.vue';
import DataTable from '@/components/tables/DataTable.vue';

import dayjs from 'dayjs';
import DateService from '@/services/dateService';
import DataService from '@/services/dataService';

// eslint-disable-next-line import/no-cycle
import FinanceApi from '@/services/apiService';

export default {
  name: 'MercadoPagoReport',
  components: { DateInput, SelectInput, DataTable },
  data() {
    return {
      search: {
        date: DateService.addDate(-7, 'days', null, 'YYYY-MM-DD'),
        country: 'MX',
      },
      paymentMethodsHeaders: [
        { key: 'payment_method', label: 'Método de Pago' },
        { key: 'quantity', label: 'Cantidad' },
        {
          key: 'total', label: 'Total (MXN)', type: 'currency', country: 'MX',
        },
        { key: 'sale_percentage', label: '% Venta', type: 'percentage' },
        { key: 'growth_percentage', label: '% Crecimiento' },
      ],
      operatorsHeaders: [
        { key: 'operator', label: 'Operador' },
        { key: 'mp_quantity', label: 'Cant MP' },
        {
          key: 'mp_total', label: 'Total MP', type: 'currency', country: 'MX',
        },
        { key: 'mp_percentage', label: '% MP', type: 'percentage' },
        { key: 'no_mp_quantity', label: 'Cant NO MP' },
        {
          key: 'no_mp_total', label: 'Total NO MP', type: 'currency', country: 'MX',
        },
        { key: 'no_mp_percentage', label: '% NO MP', type: 'percentage' },
      ],
      paymentMethodsItems: [],
      operatorsItems: [],
      dailyAnalysisItems: [],
      loading: false,
    };
  },
  computed: {
    countries() {
      return DataService.get_countries();
    },
    hasData() {
      return (this.paymentMethodsItems && this.paymentMethodsItems.length > 0)
        || (this.operatorsItems && this.operatorsItems.length > 0)
        || (this.dailyAnalysisItems && this.dailyAnalysisItems.length > 0);
    },
    countryName() {
      const country = this.countries.find((c) => c.key === this.search.country);
      return country ? country.value : this.search.country;
    },
  },
  beforeMount() {
    this.fetchMercadoPago();
  },
  methods: {
    fetchMercadoPago() {
      this.loading = true;
      // Resetear datos
      this.paymentMethodsItems = [];
      this.operatorsItems = [];
      this.dailyAnalysisItems = [];

      FinanceApi.get_mercado_pago_report(this.search).then((data) => {
        const response = data.body || {};

        // Procesar sección 1: Análisis de Métodos de Pago
        // El JSON tiene: payment_methods.methods[]
        if (response.payment_methods && response.payment_methods.methods) {
          this.paymentMethodsItems = this.mapPaymentMethodsData(
            response.payment_methods.methods,
          );
        }

        // Procesar sección 2: Análisis de Operadores
        // El JSON tiene: mercado_pago_operators.operators[]
        if (response.mercado_pago_operators && response.mercado_pago_operators.operators) {
          this.operatorsItems = this.mapOperatorsData(
            response.mercado_pago_operators.operators,
          );
        }

        // Procesar sección 3: Análisis por Día
        // El JSON tiene: daily_analysis[] (array directo)
        if (response.daily_analysis && Array.isArray(response.daily_analysis)) {
          this.dailyAnalysisItems = response.daily_analysis.map((day) => ({
            date: day.date,
            growth: day.growth,
            operators: this.mapOperatorsData(day.operators || []),
          }));
        }

        this.loading = false;
      }).catch((exception) => {
        this.loading = false;
        this.$notify.error({
          title: 'Error',
          message: exception.body?.message || exception.message || 'Error al obtener los datos',
        });
      });
    },
    formatGrowth(growth) {
      if (!growth) return '';
      if (typeof growth === 'string') return growth;
      if (typeof growth === 'object') {
        // Si viene como objeto con points y relative
        if (growth.points !== undefined && growth.relative !== undefined) {
          const pointsNum = Number(growth.points);
          const relativeNum = Number(growth.relative);
          if (!Number.isNaN(pointsNum) && !Number.isNaN(relativeNum)) {
            const pp = pointsNum >= 0
              ? `+${pointsNum.toFixed(2)}pp`
              : `${pointsNum.toFixed(2)}pp`;
            const pct = relativeNum >= 0
              ? `(+${relativeNum.toFixed(2)}%)`
              : `(${relativeNum.toFixed(2)}%)`;
            return `${pp} ${pct}`;
          }
        }
        // Si viene como objeto con percentage_points y percentage
        if (growth.percentage_points !== undefined && growth.percentage !== undefined) {
          const ppNum = Number(growth.percentage_points);
          const pctNum = Number(growth.percentage);
          if (!Number.isNaN(ppNum) && !Number.isNaN(pctNum)) {
            const pp = ppNum >= 0
              ? `+${ppNum.toFixed(2)}pp`
              : `${ppNum.toFixed(2)}pp`;
            const pct = pctNum >= 0
              ? `(+${pctNum.toFixed(2)}%)`
              : `(${pctNum.toFixed(2)}%)`;
            return `${pp} ${pct}`;
          }
        }
        // Si viene como objeto con pp y pct
        if (growth.pp !== undefined && growth.pct !== undefined) {
          const ppNum = Number(growth.pp);
          const pctNum = Number(growth.pct);
          if (!Number.isNaN(ppNum) && !Number.isNaN(pctNum)) {
            const pp = ppNum >= 0 ? `+${ppNum.toFixed(2)}pp` : `${ppNum.toFixed(2)}pp`;
            const pct = pctNum >= 0 ? `(+${pctNum.toFixed(2)}%)` : `(${pctNum.toFixed(2)}%)`;
            return `${pp} ${pct}`;
          }
        }
        return JSON.stringify(growth);
      }
      // Si es un número, asumir que es porcentaje
      if (typeof growth === 'number') {
        return growth >= 0 ? `+${growth.toFixed(2)}%` : `${growth.toFixed(2)}%`;
      }
      return String(growth);
    },
    formatDate(date) {
      if (!date) return '';
      try {
        // Usar dayjs para evitar problemas de zona horaria
        // dayjs parsea la fecha en formato YYYY-MM-DD sin cambiar el día
        const d = dayjs(date);
        if (!d.isValid()) return date;
        return d.format('DD/MM/YYYY');
      } catch (e) {
        return date;
      }
    },
    buildHeaders(keys) {
      if (Array.isArray(keys)) {
        return keys.map((key) => ({
          key: typeof key === 'string' ? key : key.key || key.field,
          label: typeof key === 'string' ? this.formatLabel(key)
            : key.label || key.headerName || this.formatLabel(key.key || key.field),
        }));
      }
      return [];
    },
    formatLabel(key) {
      return key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
    },
    mapPaymentMethodsData(data) {
      // Mapear los datos según el JSON real: method, count, total, percentage, growth_percentage
      return data.map((item) => ({
        payment_method: item.method || '',
        quantity: item.count || 0,
        total: item.total || 0,
        sale_percentage: item.percentage || 0,
        growth_percentage: item.growth_percentage || '',
      }));
    },
    mapOperatorsData(data) {
      // Mapear los datos según el JSON real: operator, mp_count, mp_total, mp_percentage,
      // no_mp_count, no_mp_total, no_mp_percentage
      return data.map((item) => ({
        operator: item.operator || '',
        mp_quantity: item.mp_count || 0,
        mp_total: item.mp_total || 0,
        mp_percentage: item.mp_percentage || 0,
        no_mp_quantity: item.no_mp_count || 0,
        no_mp_total: item.no_mp_total || 0,
        no_mp_percentage: item.no_mp_percentage || 0,
      }));
    },
  },
};
</script>

<style scoped>
.title-section {
  font-weight: bold;
  margin: 20px 0 10px 0;
  color: #303133;
}

.subtitle {
  font-size: 12px;
  color: #909399;
  margin: 0 0 15px 0;
}

.day-section {
  margin: 20px 0 10px 0;
}

.day-title {
  font-weight: bold;
  margin: 0 0 5px 0;
  color: #303133;
}

.growth-text {
  margin: 0 0 10px 0;
  color: #606266;
  font-size: 13px;
}
</style>
